import { AlertTriangle, Phone, Mail, Server, MapPin, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { LeadTableRow } from '@/services/mockLeads'

interface PatternIndicatorsProps {
  patterns: LeadTableRow['patterns']
  size?: 'sm' | 'md'
  layout?: 'horizontal' | 'vertical'
}

const patternConfig = {
  sequentialPhone: {
    icon: Phone,
    label: 'Sequential',
    description: 'Phone numbers in sequential pattern',
    color: 'text-orange-600'
  },
  suspiciousEmail: {
    icon: Mail,
    label: 'Email',
    description: 'Suspicious email domain detected',
    color: 'text-red-600'
  },
  datacenterIP: {
    icon: Server,
    label: 'Data Center',
    description: 'IP from data center or VPS',
    color: 'text-purple-600'
  },
  velocityAttack: {
    icon: Zap,
    label: 'Velocity',
    description: 'High-speed submission pattern',
    color: 'text-red-600'
  },
  zipConcentration: {
    icon: MapPin,
    label: 'Zip Cluster',
    description: 'High concentration in zip code',
    color: 'text-yellow-600'
  }
}

export function PatternIndicators({ 
  patterns, 
  size = 'md', 
  layout = 'horizontal' 
}: PatternIndicatorsProps) {
  const activePatterns = Object.entries(patterns)
    .filter(([_, isActive]) => isActive)
    .map(([key]) => key as keyof typeof patterns)
  
  if (activePatterns.length === 0) {
    return (
      <Badge variant="secondary" className="text-green-600">
        Clean
      </Badge>
    )
  }
  
  const isCompact = size === 'sm'
  
  if (layout === 'vertical') {
    return (
      <div className="space-y-1">
        {activePatterns.map((pattern) => {
          const config = patternConfig[pattern]
          const Icon = config.icon
          
          return (
            <Tooltip key={pattern}>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 p-1 rounded hover:bg-muted">
                  <Icon className={`h-3 w-3 ${config.color}`} />
                  <span className="text-xs">{config.label}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {config.description}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    )
  }
  
  // Horizontal layout
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {activePatterns.slice(0, isCompact ? 3 : 5).map((pattern) => {
        const config = patternConfig[pattern]
        const Icon = config.icon
        
        return (
          <Tooltip key={pattern}>
            <TooltipTrigger asChild>
              <Badge 
                variant="secondary" 
                className={`${config.color} bg-background border gap-1`}
              >
                <Icon className="h-3 w-3" />
                {!isCompact && config.label}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {config.description}
            </TooltipContent>
          </Tooltip>
        )
      })}
      
      {activePatterns.length > (isCompact ? 3 : 5) && (
        <Badge variant="secondary" className="text-muted-foreground">
          +{activePatterns.length - (isCompact ? 3 : 5)}
        </Badge>
      )}
      
      {activePatterns.length > 1 && (
        <div className="flex items-center gap-1 ml-1">
          <AlertTriangle className="h-3 w-3 text-red-500" />
          <span className="text-xs text-red-600 font-medium">
            Multi-Pattern
          </span>
        </div>
      )}
    </div>
  )
}